import { Button, ButtonGroup } from "@components/button";
import { formatHeaderValue } from "@components/readOnlyList/ReadOnlyList";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { getCollapsedGroupId } from "@components/smart/Smart.utils";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { createPath, IEntity } from "@odata/BindingContext";
import {
    AddressEntity,
    EntityTypeName,
    IPrAttendanceEntity,
    IPrEmploymentEntity,
    IPrEmploymentExtraSalaryEntity,
    IPrPayrollPeriodEntity,
    IPrSalaryComponentEntity,
    PrEmploymentEntity,
    PrEmploymentTemporalCurrentEntity,
    PrSalaryComponentEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    PrEmploymentCategoryCode,
    PrEmploymentTypeCode,
    PrEmploymentWorkplaceAddressSelectionCode,
    PrExtraSalaryOriginCode,
    PrPayrollPeriodStatusCode,
    PrRegularSalaryTypeCode,
    PrSalaryComponentTypeCode,
    PrSubmissionSubtypeCode,
    PrSubmissionTypeCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { setTemporalCurrentValue } from "@odata/TemporalUtils";
import TerminateEmploymentDialog from "@pages/payroll/employment/TerminateEmploymentDialog";
import { createDateFromPeriod, fetchPayrollPeriods } from "@pages/payroll/payroll.utils";
import { setupRetirementSavingsCompanyId } from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import SubmissionWizard from "@pages/payroll/submission/submissionWizard/SubmissionWizard";
import { isObjectEmpty } from "@utils/general";
import { Dayjs } from "dayjs";
import React from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import DateType, { getUtcDayjs } from "../../../types/Date";
import { IContextInitArgs } from "../../../views/formView/FormStorage";
import { IFormViewProps } from "../../../views/formView/FormView";
import { universalPersonNameFormatter } from "../../admin/users/Users.utils";
import EmploymentBaseFormView, { IEmploymentFormCustomData } from "../employmentTemplate/EmploymentBaseFormView";
import {
    createDefaultLegislativeExtraSalaryItems,
    fetchLastClosedAttendance,
    getCsszEmploymentTypesForSelectedType,
    getEmploymentCSSZSelectItems,
    initiateWithTemplate
} from "./Employment.utils";

export interface IEmploymentCustomData extends IEmploymentFormCustomData {
    lastClosedPayrollPeriod: {
        Month: number;
        Year: number;
    };
    lastClosedAttendanceDate: Dayjs;
}

interface IProps extends IFormViewProps<IPrEmploymentEntity, IEmploymentCustomData> {

}

class EmploymentFormView extends EmploymentBaseFormView<IProps> {
    static contextType = AppContext;
    static defaultProps = {
        title: "Employment:FormTitle"
    };

    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        return [
            fetchPayrollPeriods(this.props.storage.oData, { status: [PrPayrollPeriodStatusCode.Processed, PrPayrollPeriodStatusCode.ProcessedAndClosed] }),
            fetchLastClosedAttendance(this.props.storage),
            ...super.getAdditionalLoadPromise(args)
        ];
    }

    onAfterLoad(): Promise<void> {
        this.setLegalAddressAsWorkplaceAddressIfNeeded();
        const periods = this.getAdditionalResults()[0] as IPrPayrollPeriodEntity[];
        const lastClosedPeriod = periods?.[periods?.length - 1];
        if (lastClosedPeriod) {
            this.props.storage.setCustomData({
                lastClosedPayrollPeriod: {
                    Month: lastClosedPeriod.Month,
                    Year: lastClosedPeriod.Year
                }
            });
        }

        if (this.isEndedEmployment) {
            this.props.storage.data.disabled = true;
        }
        setupRetirementSavingsCompanyId(this.props.storage.data.entity);

        const lastClosedAttendance = this.getAdditionalResults()[1] as IPrAttendanceEntity;

        if (lastClosedAttendance) {
            this.props.storage.setCustomData({
                lastClosedAttendanceDate: getUtcDayjs(createDateFromPeriod(lastClosedAttendance)).endOf("month")
            });
        }

        return super.onAfterLoad();
    }

    onBeforeSave(): IEntity {
        const entity = super.onBeforeSave() as IPrEmploymentEntity;

        if (entity.WorkplaceAddressSelectionCode === PrEmploymentWorkplaceAddressSelectionCode.CompanyDefault) {
            entity.WorkplaceAddress = null;
        }

        if (entity.TypeCode === PrEmploymentTypeCode.Compensation) {
            entity.ExtraSalaries = null;
            entity.CurrentTemporalPropertyBag.WorkingPattern = null;
            entity.TemporalPropertyBag.forEach(tb => {
                tb.WorkingPattern = null;
            });
            // todo: group ranges as some might have been merged
        }

        return entity;
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            case PrSubmissionTypeCode.EmployeeRegistration:
                this.props.storage.setCustomData({ submissionType: actionId });
                this.forceUpdate();
                break;
            default:
                return super.handleCustomHeaderAction(actionId);
        }
    }

    getHeaderData = (): IReadOnlyListItem[] => {
        const { storage } = this.props;
        const { bindingContext, entity } = storage.data;

        return [
            {
                label: storage.getInfo(bindingContext.navigate(PrEmploymentEntity.Employee)).label,
                value: formatHeaderValue(universalPersonNameFormatter(entity.Employee))
            },
            {
                label: storage.getInfo(bindingContext.navigate(PrEmploymentEntity.Type)).label,
                value: formatHeaderValue(entity.Type?.Name ?? getEnumDisplayValue(EntityTypeName.PrEmploymentType, entity.TypeCode ?? entity.Type?.Code))
            },
            // todo: ... there will be Name property on the EmploymentEntity in the future
            // {
            //
            //     label: storage.getInfo(bindingContext.navigate(PrEmploymentEntity.Name)).label,
            //     value: formatHeaderValue(entity.Name)
            // }
        ];
    };

    get readOnlyListTitle(): string {
        return this.props.storage.t("Employment:FormTitle");
    }

    handleTypeChange = async (e: ISmartFieldChange) => {
        if (e.bindingContext.getPath() === PrEmploymentEntity.Type) {
            const { storage } = this.props;
            const type = e.value as PrEmploymentTypeCode;
            // find CSSZ codes available for the selected type
            const allItems = await getEmploymentCSSZSelectItems(storage);
            const availableItems = getCsszEmploymentTypesForSelectedType(allItems, type);
            // if there are some items, set the first one as default, otherwise clear the field
            if (!availableItems?.length) {
                storage.clearValueByPath(PrEmploymentEntity.CsszEmploymentType);
            } else {
                storage.setValueByPath(PrEmploymentEntity.CsszEmploymentType, availableItems?.[0]?.id);
            }

            const isDPP = type === PrEmploymentTypeCode.WorkAgreementDPP;
            const isCompensation = type === PrEmploymentTypeCode.Compensation;
            if (isDPP || isCompensation) {
                // switch regular salary component to hourly if present
                const componentsBc = storage.data.bindingContext.navigate(PrEmploymentEntity.SalaryComponents);
                const salaryComponents = storage.getValue(componentsBc) as IPrSalaryComponentEntity[];
                salaryComponents?.forEach((component, idx) => {
                    const componentType = component.TypeCode ?? component?.Type?.Code;
                    if (componentType === PrSalaryComponentTypeCode.Regular) {
                        const bc = componentsBc.addKey(component).navigate(PrSalaryComponentEntity.RegularSalaryType);
                        if (isDPP && component.RegularSalaryTypeCode !== PrRegularSalaryTypeCode.Hourly) {
                            storage.setValue(bc, PrRegularSalaryTypeCode.Hourly);
                        }
                        if (isCompensation && component.RegularSalaryTypeCode !== PrRegularSalaryTypeCode.Monthly) {
                            storage.setValue(bc, PrRegularSalaryTypeCode.Monthly);
                        }
                    }
                });
            }
            if (isDPP) {
                const isSmallScaleBc = storage.data.bindingContext
                    .navigate(PrEmploymentEntity.CurrentTemporalPropertyBag)
                    .navigate(PrEmploymentTemporalCurrentEntity.IsSmallScaleEmployment);
                setTemporalCurrentValue(storage, isSmallScaleBc, false);
            }

            this.entity.ExtraSalaries = this.entity.ExtraSalaries.filter((es: IPrEmploymentExtraSalaryEntity) => es.Default?.Type?.OriginCode !== PrExtraSalaryOriginCode.Legislative);
            await createDefaultLegislativeExtraSalaryItems(storage);
            storage.refresh();
        }
    }

    handleEmploymentCategoryChange = (e: ISmartFieldChange) => {
        if (e.bindingContext.getPath() === PrEmploymentEntity.EmploymentCategory) {
            const { storage } = this.props;
            if (storage.getValueByPath(PrEmploymentEntity.TypeCode) === PrEmploymentTypeCode.Compensation && e.value === PrEmploymentCategoryCode.Employee) {
                storage.clearValueByPath(PrEmploymentEntity.Type);
                storage.clearValueByPath(PrEmploymentEntity.TypeCode);
                storage.refreshFields();
            }
        }
    };

    async handleTemplateChange(e: ISmartFieldChange) {
        if (e.bindingContext.getPath() === PrEmploymentEntity.Template) {
            await initiateWithTemplate(this.props.storage, e.value as string);
            this.expandCurrentTemporalBagsFromDefault();
        }
    }

    setLegalAddressAsWorkplaceAddressIfNeeded(force?: boolean): void {
        const { storage } = this.props;
        const employment = storage.data.entity;
        const workplaceAddressSelection = employment.WorkplaceAddressSelectionCode;
        if (force || workplaceAddressSelection === PrEmploymentWorkplaceAddressSelectionCode.CompanyDefault) {
            const company = storage.context.getCompany();
            const legalAddress = company.LegalAddress;
            storage.setValueByPath(PrEmploymentEntity.WorkplaceAddress, legalAddress);
        }
    }

    handleWorkplaceAddressSelectionChange(e: ISmartFieldChange): void {
        if (e.bindingContext.getPath() === PrEmploymentEntity.WorkplaceAddressSelection) {
            const { storage } = this.props;
            const selection = e.value as string;
            storage.clearValueByPath(PrEmploymentEntity.WorkplaceAddress);
            const isOwn = selection === PrEmploymentWorkplaceAddressSelectionCode.Own;
            if (isOwn) {
                storage.setValueByPath(createPath(PrEmploymentEntity.WorkplaceAddress, AddressEntity.Country), CountryCode.CzechRepublic);
            } else {
                this.setLegalAddressAsWorkplaceAddressIfNeeded(true);
            }
            storage.setGroupStatus({
                isExpanded: isOwn
            }, getCollapsedGroupId(PrEmploymentEntity.WorkplaceAddressSelection));
            storage.refresh(true);
        }
    }

    handleFirstDayAtWorkDefaulting(e: ISmartFieldChange): void {
        const { storage } = this.props;
        if (e.bindingContext.getPath() === PrEmploymentEntity.DateStart && storage.data.bindingContext.isNew()) {
            // actually we are checking DateStart change and updating DateFirstDayAtWork accordingly
            const { DateFirstDayAtWork, DateStart } = storage.data.entity;
            const current = DateFirstDayAtWork && getUtcDayjs(DateFirstDayAtWork);
            const previous = DateStart && getUtcDayjs(DateStart);
            if (!current || current.isSame(previous, "day")) {
                storage.setValueByPath(PrEmploymentEntity.DateFirstDayAtWork, DateType.isValid(e.value as Date) ? e.value : null);
            }
        }
    }

    async handleChange(e: ISmartFieldChange): Promise<void> {
        // keep before storage.handleChange to have access to previous values if needed
        this.handleFirstDayAtWorkDefaulting(e);
        this.handleWorkplaceAddressSelectionChange(e);

        super.handleChange(e);
        this.handleTypeChange(e);
        this.handleEmploymentCategoryChange(e);
        await this.handleTemplateChange(e);
    }

    get submissionDefaultData(): IEntity {
        const { storage } = this.props;
        const {
            Employee: {
                FirstName,
                LastName,
                Id: EmployeeId
            },
            Id, NumberOurs
        } = storage.data.entity;
        return {
            Employee: {
                Employee: {
                    Id: EmployeeId, FirstName, LastName
                },
                FirstName,
                LastName
            },
            Employment: {
                Employment: {
                    Id, NumberOurs
                }
            }
        };
    }

    get submissionSubtype(): PrSubmissionSubtypeCode {
        return null;
    }

    get isEndedEmployment(): boolean {
        return !isObjectEmpty((this.entity as IPrEmploymentEntity)?.EmploymentClose);
    }

    /* Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */
    closeSubmissionDialog = (): void => {
        this.props.storage.setCustomData({ submissionType: null });
        this.forceUpdate();
    };

    confirmSubmissionDialog = (): void => {
        const { storage } = this.props;
        // Submission saved -> reload the form
        // todo: reload only the table?
        storage.reload({ preserveInfos: true, preserveData: true, withoutBusy: true });
        // close the dialog
        this.closeSubmissionDialog();
    };

    renderSubmissionDialog = () => {
        const { storage } = this.props;
        const { submissionType } = storage.getCustomData();
        if (submissionType) {
            return (
                <SubmissionWizard generatorKey={storage.data.bindingContext?.getKey()}
                                  onCancel={this.closeSubmissionDialog}
                                  onConfirm={this.confirmSubmissionDialog}
                                  defaultData={this.submissionDefaultData}
                                  mainType={submissionType}
                                  subType={this.submissionSubtype}/>
            );
        }
        return null;
    };

    /* /end/ Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */

    handleEndEmployment = async (): Promise<void> => {
        const { storage } = this.props;
        storage.setCustomData({
            isEndingEmployment: true
        });
        this.forceUpdate();
    };

    handleReopenEmployment = async (): Promise<void> => {
        const { storage } = this.props;
        await storage.save({
            data: {
                EmploymentClose: null
            }
        });
        await storage.reload({ preserveInfos: true, preserveData: true, withoutBusy: true });
    };

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;
        const isNewEmployment = storage.data.bindingContext.isNew();

        return (
            <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                {this.isDeletable &&
                    <SmartFormDeleteButton storage={storage}
                                           onClick={this.handleDelete}/>
                }
                {this.isEndedEmployment && <Button hotspotId={"reopenEmployment"}
                                                   isDisabled={storage.isDisabled}
                                                   onClick={this.handleReopenEmployment}
                                                   isTransparent>{storage.t("Employment:Form.ReopenEmployment")}</Button>}
                {!this.isEndedEmployment && !isNewEmployment && <Button hotspotId={"endEmployment"}
                                                    isDisabled={storage.isDisabled}
                                                    onClick={this.handleEndEmployment}
                                                    isTransparent>{storage.t("Employment:Form.EndEmployment")}</Button>}
                {this.shouldAllowCopy() && <Button hotspotId={"formCopy"}
                                                   onClick={this.handleCopy}
                                                   isDisabled={this.shouldDisableCopyButton()}
                                                   isTransparent>{storage.t("Common:Form.CreateCopy")}</Button>}
                {!isFormReadOnly &&
                    <Button hotspotId={"formSave"}
                            onClick={this.handleSaveClick}
                            isDisabled={this.shouldDisableSaveButton()}>
                        {storage.t("Common:General.Save")}
                    </Button>
                }
            </ButtonGroup>
        );
    }

    closeEndEmploymentDialog = (): void => {
        this.props.storage.setCustomData({ isEndingEmployment: false });
        this.forceUpdate();
    };

    confirmEndEmploymentDialog = async (): Promise<void> => {
        const { storage } = this.props;
        await storage.reload({ preserveInfos: true, preserveData: false, withoutBusy: false });
        this.closeEndEmploymentDialog();
    };

    renderEndEmploymentDialog = () => {
        const { storage } = this.props;
        const { isEndingEmployment } = storage.getCustomData();
        if (isEndingEmployment) {
            return (
                <TerminateEmploymentDialog onCancel={this.closeEndEmploymentDialog}
                                           bindingContext={storage.data.bindingContext}
                                           onConfirm={this.confirmEndEmploymentDialog}/>
            );
        }
        return null;
    };


    renderCustomDialogs(): React.ReactElement {
        return (
            <>
                {super.renderCustomDialogs()}
                {this.renderSubmissionDialog()}
                {this.renderEndEmploymentDialog()}
            </>);
    }
}

export default withPermissionContext(EmploymentFormView);